import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Pin, TrendingUp, TrendingDown } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { usePerformanceTrendsQuery, usePinTrendMutation } from '@/client/api/marketing/radar';
import { SearchDemandPanel } from './components/SearchDemandPanel';
import {
  useCreateTrackedKeywordMutation,
  useUpdateTrackedKeywordMutation,
} from '@/client/api/marketing/sources';
import { PLATFORM_LABEL, normalizeSocialPlatform } from '@/client/types/marketing';
import type {
  ApiPerformanceTrendItem,
  PerformanceRangeDays,
  TrendScope,
  TrendTopicKind,
} from '@/client/types/marketing/api';
import { getApiErrorMessage, getDuplicateTopic } from '@/client/utils/errors';
import { MarketingPageShell } from './components/MarketingPageShell';
import { EmptyState, Panel, StatusBadge } from './components/marketing-ui';
import { PUBLIC_RANGE_PRESETS } from './posting-insights-data';
import {
  loadPublicRangePreference,
  savePublicRangePreference,
  servedWindowLabel,
} from './performance-range-storage';

const SENTIMENT_COLORS = {
  positive: '#059669',
  neutral: '#6b7280',
  negative: '#e11d48',
} as const;

const SENTIMENT_BADGE = {
  positive: 'bg-emerald-50 text-emerald-700',
  neutral: 'bg-gray-100 text-gray-700',
  negative: 'bg-red-50 text-red-700',
} as const;

const CHANNEL_LABEL = { social: 'Social', traditional: 'Traditional', both: 'Both' } as const;
const CHANNEL_FILTERS: (ApiPerformanceTrendItem['channel'] | 'all')[] = [
  'all',
  'social',
  'traditional',
  'both',
];

const TOPIC_PLATFORMS = [
  'x',
  'instagram',
  'facebook',
  'linkedin',
  'tiktok',
  'youtube',
  'threads',
  'reddit',
] as const;

const SCOPE_OPTIONS: { id: TrendScope; label: string }[] = [
  { id: 'managed_and_discovered', label: 'All' },
  { id: 'managed', label: 'Catalog' },
  { id: 'discovered', label: 'Discovered' },
];

const KIND_OPTIONS: TrendTopicKind[] = ['hashtag', 'term', 'phrase'];

const selectCls =
  'h-9 rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-700 outline-none ring-blue-500 focus:ring-2';

function platformName(raw: string): string {
  return PLATFORM_LABEL[normalizeSocialPlatform(raw)] ?? raw;
}

function watchesInstagram(platforms?: string[]): boolean {
  return !platforms || platforms.length === 0 || platforms.includes('instagram');
}

function PlatformChips({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {TOPIC_PLATFORMS.map((p) => (
        <label key={p} className="flex items-center gap-1.5 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={value.includes(p)}
            onChange={() =>
              onChange(value.includes(p) ? value.filter((x) => x !== p) : [...value, p])
            }
            className="h-3.5 w-3.5 rounded border-gray-300"
          />
          {PLATFORM_LABEL[p]}
        </label>
      ))}
      <span className="text-[11px] text-gray-400">
        {value.length === 0 ? 'All social' : `${value.length} selected`}
      </span>
    </div>
  );
}

function ChangeCell({ changePct }: { changePct: number | null }) {
  if (changePct == null) {
    return <StatusBadge label="New" className="bg-sky-50 text-sky-800" />;
  }
  const up = changePct >= 0;
  return (
    <span className={`flex items-center gap-1 font-medium ${up ? 'text-emerald-600' : 'text-red-600'}`}>
      {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      {(changePct * 100).toFixed(0)}%
    </span>
  );
}

function PlatformBreakdown({
  rows,
}: {
  rows?: { platform: string; volume: number }[];
}) {
  if (!rows || rows.length === 0) return <span className="text-gray-400">—</span>;
  return (
    <span className="text-xs text-gray-500">
      {rows.map((r) => `${platformName(r.platform)} ${r.volume.toLocaleString()}`).join(' · ')}
    </span>
  );
}

const TrendsPage = ({ embedded = false }: { embedded?: boolean }) => {
  const [channelFilter, setChannelFilter] = useState<ApiPerformanceTrendItem['channel'] | 'all'>('all');
  const [range, setRange] = useState<PerformanceRangeDays>(() => loadPublicRangePreference());
  const [scope, setScope] = useState<TrendScope>('managed_and_discovered');
  const [platform, setPlatform] = useState<string>('all');
  const [watchOpen, setWatchOpen] = useState(false);
  const [draftLabel, setDraftLabel] = useState('');
  const [draftKind, setDraftKind] = useState<TrendTopicKind>('hashtag');
  const [draftPlatforms, setDraftPlatforms] = useState<string[]>([]);
  const [actingId, setActingId] = useState<string | null>(null);
  const [highlightTkid, setHighlightTkid] = useState<number | null>(null);
  const [editing, setEditing] = useState<ApiPerformanceTrendItem | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editKind, setEditKind] = useState<TrendTopicKind>('hashtag');
  const [editTerms, setEditTerms] = useState('');
  const [editPlatforms, setEditPlatforms] = useState<string[]>([]);
  const [editPinned, setEditPinned] = useState(true);

  useEffect(() => {
    savePublicRangePreference(range);
  }, [range]);

  const trendsQ = usePerformanceTrendsQuery({
    range_days: range,
    scope,
    limit: 20,
    ...(channelFilter !== 'all' ? { channel: channelFilter } : {}),
    ...(platform !== 'all' ? { platform } : {}),
  });

  const pin = usePinTrendMutation();
  const createKeyword = useCreateTrackedKeywordMutation();
  const updateKeyword = useUpdateTrackedKeywordMutation();
  const busy = pin.isPending || createKeyword.isPending || updateKeyword.isPending;

  const items = trendsQ.data?.items ?? [];

  useEffect(() => {
    if (highlightTkid == null) return;
    document.getElementById(`trend-tkid-${highlightTkid}`)?.scrollIntoView({ block: 'nearest' });
  }, [highlightTkid, items]);

  const volumeData = items.map((t) => ({
    label: t.label,
    volume: t.volume,
    fill: SENTIMENT_COLORS[t.sentiment],
  }));
  const rising = items.filter((t) => t.change_pct != null && t.change_pct > 0).length;
  const falling = items.filter((t) => t.change_pct != null && t.change_pct < 0).length;
  const nascent = items.filter((t) => t.change_pct == null).length;
  const quiet = items.filter((t) => t.managed && t.volume === 0).length;

  const igSkip = useMemo(
    () =>
      items.some(
        (t) => (t.kind === 'term' || t.kind === 'phrase') && watchesInstagram(t.platforms),
      ),
    [items],
  );
  const watchIgSkip = draftKind !== 'hashtag' && watchesInstagram(draftPlatforms);

  const onTopicError = (err: unknown, fallback: string) => {
    const dup = getDuplicateTopic(err);
    if (dup) {
      setWatchOpen(false);
      setScope('managed');
      setHighlightTkid(dup.tkid);
      toast.error(`Already watched (topic ${dup.tkid})`);
      return;
    }
    toast.error(getApiErrorMessage(err, fallback));
  };

  const handlers = {
    onError: (err: unknown) => onTopicError(err, 'Could not update the topic'),
  };

  const openEdit = (item: ApiPerformanceTrendItem) => {
    setEditing(item);
    setEditLabel(item.label);
    setEditKind(item.kind ?? 'hashtag');
    setEditTerms((item.terms ?? (item.term ? [item.term] : [])).join(', '));
    setEditPlatforms([...(item.platforms ?? [])]);
    setEditPinned(!!item.pinned);
  };

  const saveEdit = (e: FormEvent) => {
    e.preventDefault();
    if (!editing?.tkid) return;
    const terms = editTerms
      .split(/[,]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    updateKeyword.mutate(
      {
        tkid: editing.tkid,
        label: editLabel.trim() || editing.label,
        kind: editKind,
        terms: terms.length ? terms : [editing.term || editing.label],
        platforms: editPlatforms,
        pinned: editPinned ? 1 : 0,
      },
      {
        onSuccess: () => {
          toast.success('Topic updated');
          setEditing(null);
        },
        onError: (err) => onTopicError(err, 'Could not update the topic'),
      },
    );
  };

  const pinTopic = (item: ApiPerformanceTrendItem) => {
    setActingId(item.id);
    const done = {
      ...handlers,
      onSuccess: () => toast.success(`Pinned ${item.term || item.label}`),
      onSettled: () => setActingId(null),
    };
    if (item.tkid) {
      updateKeyword.mutate({ tkid: item.tkid, pinned: 1 }, done);
      return;
    }
    pin.mutate(
      {
        label: item.term || item.label,
        kind: item.kind ?? 'hashtag',
        platforms: item.platforms ?? [],
      },
      done,
    );
  };

  const unpinTopic = (item: ApiPerformanceTrendItem) => {
    if (!item.tkid) return;
    setActingId(item.id);
    updateKeyword.mutate(
      { tkid: item.tkid, pinned: 0 },
      {
        ...handlers,
        onSuccess: () => toast.success('Unpinned'),
        onSettled: () => setActingId(null),
      },
    );
  };

  const muteTopic = (item: ApiPerformanceTrendItem) => {
    setActingId(item.id);
    createKeyword.mutate(
      {
        label: item.label,
        terms: [item.term || item.label],
        kind: item.kind ?? 'hashtag',
        match_mode: 'any',
        channels: ['social'],
        platforms: item.platforms ?? [],
        muted: 1,
        enabled: 0,
      },
      {
        ...handlers,
        onSuccess: () => toast.success(`Muted ${item.term || item.label}`),
        onSettled: () => setActingId(null),
      },
    );
  };

  const addTopic = (e: FormEvent) => {
    e.preventDefault();
    const term = draftLabel.trim();
    if (!term) {
      toast.error('Add a hashtag, term, or phrase');
      return;
    }
    createKeyword.mutate(
      {
        label: term,
        terms: [term],
        kind: draftKind,
        match_mode: 'any',
        channels: ['social'],
        pinned: 1,
        enabled: 1,
        platforms: draftPlatforms,
      },
      {
        ...handlers,
        onSuccess: (res) => {
          const rule = res?.data?.listening_rule_id;
          toast.success(rule ? `Watching — rule ${rule}` : `Watching ${term}`);
          setDraftLabel('');
          setDraftKind('hashtag');
          setDraftPlatforms([]);
          setWatchOpen(false);
        },
      },
    );
  };

  const emptyLabel =
    scope === 'managed'
      ? 'No catalog topics yet — add a hashtag, term, or phrase.'
      : scope === 'discovered'
        ? 'No uncatalogued tags in this window.'
        : 'No trends in this window.';

  const body = (
    <>
      {!embedded && (
        <p className="mb-4 text-sm text-gray-600">
          Topics are tracked keywords. Pin compiles a listening rule; mute hides a discovered tag
          without billing a vendor. For listen-target handles, use{' '}
          <Link to="/admin/marketing/sources/listening" className="font-medium text-blue-700 hover:underline">
            Listening
          </Link>
          .
        </p>
      )}

      {igSkip && (
        <p className="mb-4 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Instagram has no public caption search. Terms and phrases watched there only hit listen-target
          accounts — a zero is a capability skip, not a quiet week.
        </p>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          {SCOPE_OPTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setScope(s.id)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                scope === s.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          {CHANNEL_FILTERS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setChannelFilter(c)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium capitalize transition-colors',
                channelFilter === c ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {c === 'all' ? 'All channels' : CHANNEL_LABEL[c]}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          {PUBLIC_RANGE_PRESETS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setRange(d)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                range === d ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {d}d
            </button>
          ))}
        </div>
        <span className="text-xs tabular-nums text-gray-400">
          {servedWindowLabel(trendsQ.data?.range_days, range)}
        </span>
        <select
          className={selectCls}
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
        >
          <option value="all">All platforms</option>
          {TOPIC_PLATFORMS.map((p) => (
            <option key={p} value={p}>
              {PLATFORM_LABEL[p]}
            </option>
          ))}
        </select>
        {trendsQ.isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
        <button
          type="button"
          onClick={() => setWatchOpen(true)}
          className="ml-auto h-9 rounded-md bg-gray-900 px-3 text-sm font-medium text-white"
        >
          Watch
        </button>
      </div>

      {trendsQ.isLoading ? (
        <div className="flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-4 py-8 text-sm text-gray-500 shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading trends…
        </div>
      ) : trendsQ.isError ? (
        <EmptyState label="Trends endpoint did not respond." />
      ) : items.length === 0 ? (
        <EmptyState label={emptyLabel} />
      ) : (
        <>
          <div className="mb-6 grid gap-4 lg:grid-cols-3">
            <Panel title="Volume by trend" className="lg:col-span-2">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={volumeData} layout="vertical" margin={{ left: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="volume" radius={[0, 4, 4, 0]} name="Mentions" fill="#0f172a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {quiet > 0 && (
                <p className="mt-2 text-xs text-gray-400">
                  {quiet} catalog {quiet === 1 ? 'topic is' : 'topics are'} at volume 0 — a real zero, not a
                  missing row.
                </p>
              )}
            </Panel>

            <Panel title="Sentiment legend">
              <ul className="space-y-3 text-sm">
                {(['positive', 'neutral', 'negative'] as const).map((s) => (
                  <li key={s} className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: SENTIMENT_COLORS[s] }} />
                    <span className="capitalize text-gray-700">{s}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-gray-400">
                Majority of classified items; unknown never votes. “New” means no prior-window volume.
                Platform split is a breakdown of volume, not a second total.
              </p>
            </Panel>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Topic</th>
                  <th className="px-4 py-3 font-medium">Kind</th>
                  <th className="px-4 py-3 font-medium">Volume</th>
                  <th className="px-4 py-3 font-medium">Change</th>
                  <th className="px-4 py-3 font-medium">By platform</th>
                  <th className="px-4 py-3 font-medium">Sentiment</th>
                  <th className="px-4 py-3 font-medium"> </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((t) => {
                  const extra = (t.terms ?? []).slice(1);
                  return (
                    <tr
                      key={t.id}
                      id={t.tkid ? `trend-tkid-${t.tkid}` : undefined}
                      className={cn(
                        'hover:bg-gray-50',
                        t.tkid && t.tkid === highlightTkid && 'bg-sky-50',
                      )}
                    >
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {t.pinned && <Pin className="h-3.5 w-3.5 text-gray-700" />}
                          <span className="font-medium text-gray-900">{t.label}</span>
                          {t.term && t.term !== t.label && (
                            <span className="text-xs text-gray-400">{t.term}</span>
                          )}
                        </div>
                        {extra.length > 0 && (
                          <p className="mt-0.5 text-xs text-gray-400">
                            Measured on first term · also {extra.join(', ')}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 capitalize text-gray-600">{t.kind ?? '—'}</td>
                      <td className={cn('px-4 py-3', t.volume === 0 ? 'text-gray-400' : 'text-gray-600')}>
                        {t.volume.toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <ChangeCell changePct={t.change_pct} />
                      </td>
                      <td className="px-4 py-3">
                        <PlatformBreakdown rows={t.by_platform} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge label={t.sentiment} className={SENTIMENT_BADGE[t.sentiment]} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          {t.managed && t.pinned ? (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => unpinTopic(t)}
                              className="text-xs font-medium text-gray-600 hover:underline disabled:opacity-50"
                            >
                              {actingId === t.id ? '…' : 'Unpin'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => pinTopic(t)}
                              className="text-xs font-medium text-blue-700 hover:underline disabled:opacity-50"
                            >
                              {actingId === t.id ? '…' : 'Pin'}
                            </button>
                          )}
                          {!t.managed && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => muteTopic(t)}
                              className="text-xs font-medium text-gray-600 hover:underline disabled:opacity-50"
                            >
                              Mute
                            </button>
                          )}
                          {t.managed && t.tkid != null && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => openEdit(t)}
                              className="text-xs font-medium text-gray-600 hover:underline disabled:opacity-50"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog
        open={watchOpen}
        onOpenChange={(open) => {
          setWatchOpen(open);
          if (!open) {
            setDraftLabel('');
            setDraftKind('hashtag');
            setDraftPlatforms([]);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Watch a topic</DialogTitle>
          </DialogHeader>
          <form onSubmit={addTopic} className="space-y-3">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-gray-600">Term</span>
              <input
                value={draftLabel}
                onChange={(e) => setDraftLabel(e.target.value)}
                placeholder={draftKind === 'hashtag' ? '#scamstory' : 'term or phrase'}
                className="h-9 w-full rounded-md border border-gray-200 px-2.5 text-sm outline-none ring-blue-500 focus:ring-2"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-gray-600">Kind</span>
              <select
                className={`${selectCls} w-full`}
                value={draftKind}
                onChange={(e) => setDraftKind(e.target.value as TrendTopicKind)}
              >
                {KIND_OPTIONS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </label>
            <div className="space-y-1">
              <span className="text-xs font-medium text-gray-600">Platforms</span>
              <PlatformChips value={draftPlatforms} onChange={setDraftPlatforms} />
            </div>
            {watchIgSkip && (
              <p className="rounded-md border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Instagram has no public caption search. Terms and phrases watched there only hit
                listen-target accounts.
              </p>
            )}
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={busy}>
                Watch
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit topic</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-gray-600">Label</span>
              <input
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                className="h-9 w-full rounded-md border border-gray-200 px-2.5 text-sm outline-none ring-blue-500 focus:ring-2"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-gray-600">Kind</span>
              <select
                className={`${selectCls} w-full`}
                value={editKind}
                onChange={(e) => setEditKind(e.target.value as TrendTopicKind)}
              >
                {KIND_OPTIONS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-gray-600">Terms</span>
              <input
                value={editTerms}
                onChange={(e) => setEditTerms(e.target.value)}
                className="h-9 w-full rounded-md border border-gray-200 px-2.5 text-sm outline-none ring-blue-500 focus:ring-2"
                placeholder="#scamstory, second term"
              />
              <span className="text-[11px] text-gray-400">Volume is measured on the first term.</span>
            </label>
            <div className="space-y-1">
              <span className="text-xs font-medium text-gray-600">Platforms</span>
              <PlatformChips value={editPlatforms} onChange={setEditPlatforms} />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={editPinned}
                onChange={(e) => setEditPinned(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Pinned (stays on the report at volume 0)
            </label>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={busy}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <SearchDemandPanel />
    </>
  );

  if (embedded) return body;

  return (
    <MarketingPageShell
      title="Trends"
      description="Hashtags, terms, and phrases Radar is watching — catalog left-joined onto coverage. Window is 1–90 days, clamped."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Radar', href: '/admin/marketing/radar' },
        { label: 'Performance', href: '/admin/marketing/radar/performance' },
        { label: 'Trends' },
      ]}
      icon={TrendingUp}
      stats={[
        { key: 'total', label: 'Topics', value: items.length },
        { key: 'rising', label: 'Rising', value: rising },
        { key: 'falling', label: 'Falling', value: falling },
        { key: 'new', label: 'New', value: nascent },
      ]}
    >
      {body}
    </MarketingPageShell>
  );
};

export default TrendsPage;
